import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ListChecks,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import {
  cn,
  formatRelativeDate,
  fullName,
  isPast,
  todayISO,
} from "@/lib/utils";
import type { Prospect, Todo } from "@shared/schema";

export function TodoPage() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [reschedule, setReschedule] = useState<Prospect | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([api.listProspects(), api.listTodos()]);
      setProspects(p.prospects);
      setTodos(t.todos);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const relances = useMemo(
    () =>
      prospects
        .filter(
          (p) =>
            p.nextFollowUpAt &&
            p.status !== "closed_won" &&
            p.status !== "closed_lost",
        )
        .sort((a, b) => (a.nextFollowUpAt! < b.nextFollowUpAt! ? -1 : 1)),
    [prospects],
  );

  const overdueCount = relances.filter((p) => isPast(p.nextFollowUpAt)).length;
  const openTodos = todos.filter((t) => !t.done).length;

  return (
    <>
      <PageHeader
        title="To-Do"
        subtitle="Les relances à faire et tes tâches du moment"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Relances à faire */}
        <section className="surface p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-navy-700)] flex items-center gap-2">
              <RotateCcw size={18} /> Relances à faire
            </h2>
            <span className="text-xs text-[var(--color-muted-foreground)] tabular">
              {relances.length}
              {overdueCount > 0 && (
                <span className="text-[var(--color-coral-600)] font-medium">
                  {" "}
                  · {overdueCount} en retard
                </span>
              )}
            </span>
          </div>

          {loading ? (
            <p className="text-sm text-[var(--color-muted-foreground)] py-4">
              Chargement…
            </p>
          ) : relances.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)] text-center py-8">
              Aucune relance planifiée. 🎉
            </p>
          ) : (
            <ul className="space-y-2">
              {relances.map((p) => {
                const overdue = isPast(p.nextFollowUpAt);
                return (
                  <li
                    key={p.id}
                    className={cn(
                      "flex items-stretch gap-3 rounded-[var(--radius-md)] border transition-colors",
                      "border-[color-mix(in_srgb,#7c3aed_30%,white)] bg-[color-mix(in_srgb,#7c3aed_8%,white)]",
                      overdue && "ring-2 ring-[var(--color-coral-500)]",
                    )}
                  >
                    <div className="w-1.5 rounded-l-[var(--radius-md)] bg-[#7c3aed]" />
                    <div className="flex-1 min-w-0 py-2.5 pr-2">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 sm:flex-nowrap">
                        <Link
                          to={`/prospects/${p.id}`}
                          className="font-semibold text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] max-sm:basis-full sm:truncate"
                        >
                          {fullName(p)}
                        </Link>
                        <span
                          className={cn(
                            "text-xs tabular font-medium",
                            overdue
                              ? "text-[var(--color-coral-600)]"
                              : "text-[#5b21b6]",
                          )}
                        >
                          {overdue && (
                            <AlertCircle
                              size={12}
                              className="inline mr-0.5 -mt-0.5"
                            />
                          )}
                          {formatRelativeDate(p.nextFollowUpAt)}
                        </span>
                        <StatusPill status={p.status} className="ml-auto" />
                      </div>
                      {p.context && (
                        <p className="text-xs text-[var(--color-muted-foreground)] truncate mt-0.5">
                          {p.context.split(/\n/)[0]}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setReschedule(p)}
                      className="btn btn-ghost btn-sm self-center mr-2 text-[var(--color-navy-600)] max-sm:h-11"
                      title="Relance traitée"
                    >
                      <Check size={15} /> Fait
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Mes tâches */}
        <section className="surface p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-navy-700)] flex items-center gap-2">
              <ListChecks size={18} /> Mes tâches
            </h2>
            <span className="text-xs text-[var(--color-muted-foreground)] tabular">
              {openTodos} à faire
            </span>
          </div>

          <NewTodoForm onCreated={load} />

          <ul className="mt-4 space-y-1.5">
            {todos.length === 0 && !loading && (
              <li className="text-sm text-[var(--color-muted-foreground)] text-center py-6">
                Pas encore de tâche. Ajoute la première ci-dessus.
              </li>
            )}
            {todos.map((t) => (
              <TodoRow key={t.id} todo={t} onChange={load} />
            ))}
          </ul>
        </section>
      </div>

      <Modal
        open={!!reschedule}
        onClose={() => setReschedule(null)}
        title="Relance traitée"
        description={
          reschedule
            ? `Reprogrammer la prochaine relance pour ${fullName(reschedule)} ?`
            : undefined
        }
        size="sm"
      >
        {reschedule && (
          <RescheduleForm
            prospect={reschedule}
            onDone={() => {
              setReschedule(null);
              load();
            }}
          />
        )}
      </Modal>
    </>
  );
}

function RescheduleForm({
  prospect,
  onDone,
}: {
  prospect: Prospect;
  onDone: () => void;
}) {
  const [date, setDate] = useState(prospect.nextFollowUpAt ?? todayISO());
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function apply(nextFollowUpAt: string | null) {
    setBusy(true);
    try {
      await api.updateProspect(
        prospect.id,
        nextFollowUpAt ? { nextFollowUpAt } : { nextFollowUpAt: null, nextAction: null },
      );
      toast.success(
        nextFollowUpAt ? "Relance reprogrammée" : "Relance terminée",
      );
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide mb-1.5">
          Prochaine relance
        </label>
        <input
          type="date"
          className="input-base"
          value={date}
          min={todayISO()}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          className="btn btn-primary flex-1"
          disabled={busy || !date}
          onClick={() => apply(date)}
        >
          <RotateCcw size={15} /> Reprogrammer
        </button>
        <button
          type="button"
          className="btn btn-outline flex-1"
          disabled={busy}
          onClick={() => apply(null)}
        >
          <CheckCircle2 size={15} /> Plus de relance
        </button>
      </div>
    </div>
  );
}

function NewTodoForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.createTodo({ title: title.trim(), dueAt: dueAt || null });
      setTitle("");
      setDueAt("");
      onCreated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
      <input
        className="input-base sm:flex-1"
        placeholder="Nouvelle tâche…"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <input
        type="date"
        className="input-base sm:w-40"
        value={dueAt}
        onChange={(e) => setDueAt(e.target.value)}
        title="Échéance (optionnel)"
      />
      <button
        type="submit"
        className="btn btn-primary"
        disabled={busy || !title.trim()}
      >
        <Plus size={16} /> Ajouter
      </button>
    </form>
  );
}

function TodoRow({ todo, onChange }: { todo: Todo; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const overdue = !todo.done && isPast(todo.dueAt);

  async function toggle() {
    setBusy(true);
    try {
      await api.updateTodo(todo.id, { done: !todo.done });
      onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.deleteTodo(todo.id);
      onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="flex items-center gap-3 px-2 py-2 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-2)] group transition-colors">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-label={todo.done ? "Marquer non fait" : "Marquer fait"}
        className={cn(
          "h-5 w-5 rounded-[6px] border flex-shrink-0 grid place-items-center transition-colors",
          todo.done
            ? "bg-[#16a34a] border-[#15803d] text-white"
            : "bg-white border-[var(--color-border-strong)] hover:border-[var(--color-navy-400)]",
        )}
      >
        {todo.done && <Check size={13} />}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "text-sm truncate",
            todo.done
              ? "text-[var(--color-muted-foreground)] line-through"
              : "text-[var(--color-foreground)]",
          )}
        >
          {todo.title}
        </div>
        {todo.dueAt && (
          <div
            className={cn(
              "text-xs tabular",
              overdue
                ? "text-[var(--color-coral-600)] font-medium"
                : "text-[var(--color-muted-foreground)]",
            )}
          >
            {overdue && <AlertCircle size={11} className="inline mr-0.5 -mt-0.5" />}
            {formatRelativeDate(todo.dueAt)}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={remove}
        disabled={busy}
        aria-label="Supprimer"
        className="btn btn-ghost btn-icon btn-sm opacity-0 group-hover:opacity-100 text-[var(--color-muted-foreground)]"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}
